import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import VisualInsights from "../components/VisualInsights";
import WinLossPieChart from "../components/WinLossPieChart";

const Overview = () => {
  const { user, api } = useAuth();
  const [error, setError] = useState(null);
  const [riskRows, setRiskRows] = useState([]);
  const [statsLoading, setStatsLoading] = useState(true);

  const isGuest =
    !user ||
    user.isGuest === true ||
    user.role === "guest" ||
    user.type === "guest" ||
    user.email === "guest@example.com";

  useEffect(() => {
    if (isGuest) return;

    let mounted = true;

    (async () => {
      try {
        setStatsLoading(true);
        const rows = await api("/api/risk", { method: "GET" });
        if (mounted) {
          setRiskRows(Array.isArray(rows) ? rows : []);
        }
      } catch (err) {
        console.error("[Overview] Failed to load risk stats:", err);
        if (mounted) {
          setRiskRows([]);
        }
      } finally {
        if (mounted) setStatsLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [api, isGuest]);

  const stats = useMemo(() => {
    const totalCalcs = riskRows.length;

    const avgRiskPercent =
      totalCalcs > 0
        ? riskRows.reduce(
            (sum, row) =>
              sum +
              Number(
                row.risk_engine_risk_percent ??
                  row.riskEngineRiskPercent ??
                  row.risk_per_trade ??
                  row.riskPct ??
                  0
              ),
            0
          ) / totalCalcs
        : 0;

    const avgRR =
      totalCalcs > 0
        ? riskRows.reduce(
            (sum, row) =>
              sum +
              Number(
                row.risk_engine_rr ??
                  row.riskEngineRR ??
                  row.rr_ratio ??
                  row.rrRatio ??
                  0
              ),
            0
          ) / totalCalcs
        : 0;

    const avgScore =
      totalCalcs > 0
        ? riskRows.reduce(
            (sum, row) =>
              sum + Number(row.risk_engine_score ?? row.riskEngineScore ?? 0),
            0
          ) / totalCalcs
        : 0;

    const withinRules = riskRows.filter((row) => {
      const status = row.risk_engine_status ?? row.riskEngineStatus;
      return status === "within_preferred_rules";
    }).length;

    const outsideRules = riskRows.filter((row) => {
      const status = row.risk_engine_status ?? row.riskEngineStatus;
      return status === "outside_preferred_rules";
    }).length;

    return {
      totalCalcs,
      avgRiskPercent,
      avgRR,
      avgScore,
      withinRules,
      outsideRules,
    };
  }, [riskRows]);

  const statCards = [
    {
      label: "Saved Calculations",
      value: statsLoading ? "..." : stats.totalCalcs,
    },
    {
      label: "Average Risk %",
      value: statsLoading ? "..." : `${stats.avgRiskPercent.toFixed(1)}%`,
    },
    {
      label: "Average R:R",
      value: statsLoading ? "..." : stats.avgRR.toFixed(2),
    },
    {
      label: "Average Risk Score",
      value: statsLoading ? "..." : stats.avgScore.toFixed(0),
    },
  ];

  const recentRiskActivity = useMemo(() => {
    return [...riskRows]
      .sort((a, b) => {
        const aDate = new Date(a.created_at ?? a.createdAt ?? 0).getTime();
        const bDate = new Date(b.created_at ?? b.createdAt ?? 0).getTime();
        return bDate - aDate;
      })
      .slice(0, 5);
  }, [riskRows]);

  const formatNumber = (value, digits = 2) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return "-";
    return parsed.toFixed(digits);
  };

  const formatCurrency = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return "-";
    return `$${parsed.toFixed(0)}`;
  };

  const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleDateString();
  };

  const getStatusLabel = (row) => {
    const status = row.risk_engine_status ?? row.riskEngineStatus;
    if (status === "within_preferred_rules") return "Within Preferred Rules";
    if (status === "outside_preferred_rules") return "Outside Preferred Rules";
    return "No Status";
  };

  const getStatusClass = (row) => {
    const status = row.risk_engine_status ?? row.riskEngineStatus;
    if (status === "within_preferred_rules") return "text-green-400";
    if (status === "outside_preferred_rules") return "text-yellow-400";
    return "text-gray-400";
  };

  if (isGuest) {
    return (
      <div className="p-6 text-white">
        <div className="mx-auto max-w-4xl rounded-2xl border border-white/10 bg-[#171717] p-8 text-center shadow-lg">
          <p className="text-xs uppercase tracking-[0.2em] text-blue-400 mb-3">
            Signed-In Access Required
          </p>

          <h2 className="text-2xl font-semibold mb-4">Overview</h2>

          <p className="text-sm md:text-base text-gray-400 max-w-2xl mx-auto leading-8">
            The Overview dashboard is only available with a signed-in FlowQuant
            account. Sign in or register to unlock win/loss analytics,
            performance insights, and a more connected trading workflow.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 text-white">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="rounded-2xl border border-white/10 bg-[#171717] p-6 shadow-lg md:p-8">
          <p className="text-xs uppercase tracking-[0.2em] text-blue-400 mb-3">
            Signed-In Dashboard
          </p>

          <h2 className="text-2xl font-semibold mb-3">Overview</h2>

          <p className="text-sm md:text-base text-gray-400 max-w-3xl leading-8">
            Track your performance through a cleaner dashboard built around
            visual insights, win/loss behavior, and a more connected view of
            your trading workflow.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {statCards.map((card) => (
            <div
              key={card.label}
              className="rounded-2xl border border-white/10 bg-[#171717] p-5 shadow-lg"
            >
              <p className="text-xs uppercase tracking-[0.18em] text-blue-400 mb-2">
                {card.label}
              </p>
              <p className="text-2xl font-semibold text-white">{card.value}</p>
            </div>
          ))}
        </div>

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-2">
          <div className="rounded-2xl border border-white/10 bg-[#171717] p-5 shadow-lg">
            <p className="text-xs uppercase tracking-[0.18em] text-blue-400 mb-2">
              Within Preferred Rules
            </p>
            <p className="text-2xl font-semibold text-white">
              {statsLoading ? "..." : stats.withinRules}
            </p>
          </div>

          <div className="rounded-2xl border border-white/10 bg-[#171717] p-5 shadow-lg">
            <p className="text-xs uppercase tracking-[0.18em] text-blue-400 mb-2">
              Outside Preferred Rules
            </p>
            <p className="text-2xl font-semibold text-white">
              {statsLoading ? "..." : stats.outsideRules}
            </p>
          </div>
        </div>

        {error && (
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            Couldn&apos;t load analytics data — showing zero state.
          </div>
        )}

        <div className="grid gap-6 lg:grid-cols-[1.5fr_1fr]">
          <div className="rounded-2xl border border-white/10 bg-[#171717] p-6 shadow-lg">
            <p className="text-xs uppercase tracking-[0.2em] text-blue-400 mb-3">
              Performance Insights
            </p>
            <VisualInsights onError={setError} />
          </div>

          <div className="rounded-2xl border border-white/10 bg-[#171717] p-6 shadow-lg">
            <p className="text-xs uppercase tracking-[0.2em] text-blue-400 mb-3 text-center">
              Win / Loss Snapshot
            </p>
            <div className="flex justify-center">
              <WinLossPieChart onError={setError} />
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#171717] p-6 shadow-lg">
          <p className="text-xs uppercase tracking-[0.2em] text-blue-400 mb-3">
             Activity Feed
          </p>

          <h3 className="text-xl font-semibold mb-4">Recent Risk Activity</h3>

          {statsLoading ? (
            <p className="text-sm text-gray-400">Loading recent activity...</p>
          ) : recentRiskActivity.length === 0 ? (
            <p className="text-sm text-gray-400">
              No saved calculations yet. Once you save trades, they’ll show up here.
            </p>
          ) : (
            <div className="space-y-3">
              {recentRiskActivity.map((row) => (
                <div
                  key={row.id}
                  className="rounded-xl border border-white/10 bg-[#121212] px-4 py-4"
                >
                  <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                    <div>
                      <p className="text-base font-semibold text-white">
                        {row.instrument ?? row.ticker ?? "Unknown Symbol"}
                      </p>
                      <p className="text-sm text-gray-400">
                        Saved {formatDate(row.created_at ?? row.createdAt)}
                      </p>
                    </div>

                    <p className={`text-sm font-medium ${getStatusClass(row)}`}>
                      {getStatusLabel(row)}
                    </p>
                  </div>

                  <div className="mt-3 grid gap-3 text-sm text-gray-300 sm:grid-cols-2 lg:grid-cols-4">
                    <p>
                      <span className="text-gray-500">Risk %:</span>{" "}
                      {formatNumber(
                        row.risk_engine_risk_percent ??
                          row.riskEngineRiskPercent ??
                          row.risk_per_trade ??
                          row.riskPct ??
                          0,
                        1
                      )}
                      %
                    </p>
                    <p>
                      <span className="text-gray-500">R:R:</span>{" "}
                      {formatNumber(
                        row.risk_engine_rr ??
                          row.riskEngineRR ??
                          row.rr_ratio ??
                          row.rrRatio ??
                          0,
                        2
                      )}
                    </p>
                    <p>
                      <span className="text-gray-500">Score:</span>{" "}
                      {formatNumber(
                        row.risk_engine_score ?? row.riskEngineScore ?? 0,
                        0
                      )}
                    </p>
                    <p>
                      <span className="text-gray-500">Risk Amount:</span>{" "}
                      {formatCurrency(row.risk_amount ?? row.riskAmount ?? 0)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Overview;